import {Box, FormControl, InputLabel, MenuItem, Select, Typography} from '@mui/material';
import {useCallback} from 'react';
import {ModuleTopic} from '@cp/model';
import mapModuleTopicToTopicString from '../../utils/map/mapModuleTopicToTopicString';
import useDataExtractorAndValidator from '../../hooks/helper/useDataExtractorAndValidator';

export interface DropdownTopicProps {
  /** The value of the topic selected */
  value?: string | null;
  /** The function to call when the topic is changed */
  onChange: (value?: string) => void;
  /** The list of available topics to display in the dropdown */
  availableTopics: ModuleTopic[];
  /** If true, the dropdown will be disabled */
  disabled?: boolean;
  /** The label of the dropdown (default: Topic) */
  label?: string;
  /** If true, the dropdown will be required */
  required?: boolean;
  /** give id of component */
  id?: string;
  /** show module topic detail field */
  showDetail?: boolean;
  /** Whether to show "All Topics" option (for optional topic) */
  allowAll?: boolean;
}

export default function DropdownTopic({
  value,
  onChange,
  availableTopics,
  disabled = false,
  label = 'Topic',
  required = false,
  id,
  showDetail = false,
  allowAll = false,
}: DropdownTopicProps) {
  const handleChange = useCallback(
    (e: any) => {
      onChange(e.target.value || null);
    },
    [onChange],
  );

  const {selected: validatedTopic} = useDataExtractorAndValidator<ModuleTopic, string>(
    availableTopics,
    value || undefined,
    (topic) => topic.topic,
    (topicId, selectedTopicId) => topicId === selectedTopicId,
    '',
  );

  return (
    <FormControl fullWidth data-testid="dropdown-topic">
      <InputLabel>
        {label}
        {required ? ' *' : ''}
      </InputLabel>
      <Select
        id={id}
        value={validatedTopic}
        onChange={handleChange}
        label={label + (required ? ' *' : '')}
        disabled={disabled || availableTopics.length === 0}
        renderValue={(selected) => {
          if (!selected) return allowAll ? 'All Topics' : '';
          const topic = availableTopics.find((t) => t.topic === selected);
          return topic ? mapModuleTopicToTopicString(topic, {spacer: ' • '}) : selected;
        }}
      >
        {allowAll && (
          <MenuItem value="">
            <em>All Topics</em>
          </MenuItem>
        )}
        {availableTopics.map((topic) => (
          <MenuItem key={`DropdownTopic-${topic.topic}`} value={topic.topic}>
            <Box>
              <Typography variant="body2" sx={{fontWeight: 500}}>
                {topic.category}
                {topic.topic ? ` • ${topic.topic}` : ''}
              </Typography>
              {showDetail && topic.detail && (
                <Typography variant="caption" sx={{color: '#666', display: 'block'}}>
                  {topic.detail}
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
